import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Clock, User, LogOut, Star, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Message {
  id: string;
  content: string;
  is_admin: boolean;
  created_at: string;
}

export function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'chat' | 'review' | 'thanks'>('form');
  const [preChatName, setPreChatName] = useState('');
  const [preChatEmail, setPreChatEmail] = useState('');
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize session and prefill
  useEffect(() => {
    const initSession = async () => {
      const storedSession = localStorage.getItem('nexqr_support_session');
      if (storedSession) {
        setSessionId(storedSession);
        fetchMessages(storedSession);
        setStep('chat');
      }
    };
    initSession();

    if (user) {
      setPreChatEmail(user.email || '');
      setPreChatName(user.user_metadata?.full_name || '');
    }
  }, [user]);

  // Real-time subscription
  useEffect(() => {
    if (!sessionId || !isOpen) return;

    const channel = supabase
      .channel(`chat:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const newMessage = payload.new as Message;
          setMessages((prev) => {
            if (prev.find(m => m.id === newMessage.id)) return prev;
            return [...prev, newMessage];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, isOpen]);

  const fetchMessages = async (sid: string) => {
    const { data, error } = await (supabase as any)
      .from('chat_messages' as any)
      .select('*')
      .eq('session_id', sid)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setMessages(data);
    }
  };

  const startNewSession = async (initialMessage?: string) => {
    const { data, error } = await (supabase as any)
      .from('chat_sessions' as any)
      .insert({
        user_id: user?.id || null,
        guest_name: preChatName || user?.user_metadata?.full_name || 'Guest',
        guest_email: preChatEmail || user?.email || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Support Chat Error:', error);
      toast.error('Failed to start chat session: ' + error.message);
      return null;
    }

    localStorage.setItem('nexqr_support_session', data.id);
    setSessionId(data.id);
    setStep('chat');

    if (initialMessage) {
      await (supabase as any).from('chat_messages' as any).insert({
        session_id: data.id,
        sender_id: user?.id || null,
        content: initialMessage.trim(),
        is_admin: false,
      });
    }

    return data.id;
  };

  const handleStartChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preChatName.trim() || !preChatEmail.trim()) {
      toast.error('Please provide your name and email');
      return;
    }
    setIsLoading(true);
    await startNewSession();
    setIsLoading(false);
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    setIsLoading(true);
    let currentSid = sessionId;

    if (!currentSid) {
      currentSid = await startNewSession();
    }

    if (!currentSid) {
      setIsLoading(false);
      return;
    }

    const { error } = await (supabase as any).from('chat_messages' as any).insert({
      session_id: currentSid,
      sender_id: user?.id || null,
      content: inputValue.trim(),
      is_admin: false,
    });

    if (error) {
      toast.error('Failed to send message');
    } else {
      setInputValue('');
      
      // Handle "Admin Offline" auto-reply logic
      const hasAdminReply = messages.some(m => m.is_admin);
      const isFirstUserMessage = messages.filter(m => !m.is_admin).length === 0;

      if (!hasAdminReply && isFirstUserMessage) {
        // Show waiting message after a short delay to feel real
        setTimeout(() => {
          const waitingMsg: Message = {
            id: 'waiting-msg-' + Date.now(),
            content: "Our support team is currently assisting other users. Average waiting time is ~5 minutes. We've notified our agents and they'll be with you shortly!",
            is_admin: true,
            created_at: new Date().toISOString()
          };
          setMessages(prev => [...prev, waitingMsg]);
        }, 3000);
      }
    }
    setIsLoading(false);
  };

  const handleEndChat = async () => {
    if (!sessionId) return;
    setIsLoading(true);
    
    // Update status in DB
    const { error } = await (supabase as any)
      .from('chat_sessions' as any)
      .update({ status: 'ended', updated_at: new Date().toISOString() })
      .eq('id', sessionId);

    if (error) {
      console.error('Error ending chat:', error);
      toast.error('Failed to end chat in database');
    }

    setStep('review');
    localStorage.removeItem('nexqr_support_session');
    setIsLoading(false);
  };

  const handleSubmitReview = async () => {
    if (!sessionId || rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setIsLoading(true);
    const { error } = await (supabase as any)
      .from('chat_sessions' as any)
      .update({ rating, feedback, updated_at: new Date().toISOString() })
      .eq('id', sessionId);

    if (error) {
      toast.error('Failed to save review');
    } else {
      setStep('thanks');
      setTimeout(() => {
        setIsOpen(false);
        // Reset for next time
        setSessionId(null);
        setStep('form');
        setMessages([]);
      }, 3000);
    }
    setIsLoading(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 active:scale-95",
          isOpen ? "bg-destructive text-white" : "bg-primary text-white"
        )}
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] md:w-[420px] h-[70vh] sm:h-[500px] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 bg-primary text-primary-foreground flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center p-1.5 overflow-hidden shadow-inner">
                    <img src="/logo.png" alt="NexQR Logo" className="w-full h-full object-contain" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-primary rounded-full" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">NexQR Support</h3>
                  <p className="text-[10px] opacity-80 uppercase tracking-widest font-semibold">Online 24/7</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {step === 'chat' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleEndChat}
                    className="h-8 w-8 text-primary-foreground hover:bg-white/20 rounded-full"
                    title="End Chat"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                )}
                <div className="flex items-center gap-1 text-[10px] bg-white/10 px-2 py-1 rounded-full">
                  <Clock className="h-3 w-3" />
                  <span>~5m wait</span>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {step === 'form' ? (
                <div className="p-6 flex flex-col h-full">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 p-3 shadow-inner">
                      <img src="/logo.png" alt="NexQR Logo" className="w-full h-full object-contain" />
                    </div>
                    <h3 className="font-bold text-lg">Welcome to Support</h3>
                    <p className="text-sm text-muted-foreground">
                      Please introduce yourself to start chatting with our team.
                    </p>
                  </div>

                  <form onSubmit={handleStartChat} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="pre-name" className="text-xs uppercase tracking-wider font-bold opacity-70">Your Name</Label>
                      <Input
                        id="pre-name"
                        placeholder="John Doe"
                        value={preChatName}
                        onChange={(e) => setPreChatName(e.target.value)}
                        required
                        className="bg-muted/50 border-none h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pre-email" className="text-xs uppercase tracking-wider font-bold opacity-70">Email Address</Label>
                      <Input
                        id="pre-email"
                        type="email"
                        placeholder="john@example.com"
                        value={preChatEmail}
                        onChange={(e) => setPreChatEmail(e.target.value)}
                        required
                        className="bg-muted/50 border-none h-11"
                        disabled={!!user}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full gradient-hero h-11 font-bold mt-4 shadow-lg hover:shadow-xl transition-shadow"
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : "Start Conversation"}
                    </Button>
                  </form>
                </div>
              ) : step === 'review' ? (
                <div className="p-6 flex flex-col h-full overflow-y-auto">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-yellow-400/10 rounded-full flex items-center justify-center mx-auto mb-4 text-yellow-500">
                      <Star className="h-8 w-8 fill-current" />
                    </div>
                    <h3 className="font-bold text-lg">Rate your experience</h3>
                    <p className="text-sm text-muted-foreground">
                      How did we do? Your feedback helps us improve.
                    </p>
                  </div>

                  <div className="flex justify-center gap-2 mb-8">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        onClick={() => setRating(s)}
                        className={cn(
                          "p-1 transition-all transform hover:scale-125",
                          rating >= s ? "text-yellow-500" : "text-muted-foreground opacity-30"
                        )}
                      >
                        <Star className={cn("h-8 w-8", rating >= s && "fill-current")} />
                      </button>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider font-bold opacity-70">Additional Feedback (Optional)</Label>
                      <Textarea
                        placeholder="Tell us more about your experience..."
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        className="bg-muted/50 border-none min-h-[100px] resize-none"
                      />
                    </div>
                    <Button 
                      onClick={handleSubmitReview}
                      className="w-full gradient-hero h-11 font-bold mt-4"
                      disabled={isLoading || rating === 0}
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : "Submit Review"}
                    </Button>
                  </div>
                </div>
              ) : step === 'thanks' ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 animate-in zoom-in duration-300">
                  <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6 text-green-500">
                    <CheckCircle2 className="h-12 w-12" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Thank You!</h3>
                  <p className="text-muted-foreground text-sm">
                    Your feedback has been received. Have a wonderful day!
                  </p>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.length === 0 && (
                        <div className="text-center py-8 space-y-2">
                          <div className="w-12 h-12 bg-primary/5 rounded-full flex items-center justify-center mx-auto text-primary">
                            <MessageCircle className="h-6 w-6" />
                          </div>
                          <p className="text-sm font-medium">Hello {preChatName.split(' ')[0]}!</p>
                          <p className="text-xs text-muted-foreground">How can we help you today?</p>
                        </div>
                      )}
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex flex-col max-w-[80%]",
                            msg.is_admin ? "self-start" : "self-end items-end ml-auto"
                          )}
                        >
                          <div
                            className={cn(
                              "px-4 py-2 rounded-2xl text-sm",
                              msg.is_admin
                                ? "bg-muted text-foreground rounded-tl-none"
                                : "bg-primary text-primary-foreground rounded-tr-none"
                            )}
                          >
                            {msg.content}
                          </div>
                          <span className="text-[10px] text-muted-foreground mt-1">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <div className="p-4 border-t border-border bg-muted/30">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSend();
                      }}
                      className="flex gap-2"
                    >
                      <Input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Type a message..."
                        className="bg-background border-none shadow-sm focus-visible:ring-primary"
                        disabled={isLoading}
                      />
                      <Button 
                        size="icon" 
                        type="submit" 
                        disabled={!inputValue.trim() || isLoading}
                        className="shrink-0"
                      >
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </form>
                    <p className="text-[10px] text-center text-muted-foreground mt-2">
                      By messaging, you agree to our <a href="/terms" className="underline">Support Terms</a>
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
